import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PageInfoService, PageLink } from '@metronic/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { QrProductsService } from '../qr-products.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { IQRBatchDetailItem, IQrBatchItem } from '../qr-products.interface';
import { BaseResponse } from '@shared/base/base-response';
import { EnumDetailBatchInfo } from '../enum/label-value.enum';
import { UtilitiesService } from '@services/utilities.service';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { TableColumn } from '@shared/interface/table.interface';
import { IGenericLabelValue } from '@shared/interface/generic';
import { BaseTableService } from '@shared/base/base-table-service.service';
import { BaseDatasource } from '@shared/base/base.datasource';
import { IQueryParams } from '@shared/interface/urlparam.interface';
import { API } from '@config/constants/api.constant';
import { ModalConfirmDownloadComponent } from '../components/modal-confirm-download/modal-confirm-download.component';
import { UrlUtilsService } from '@utils/url-utils.service';

@Component({
  selector: 'app-qr-batch-detail',
  templateUrl: './qr-batch-detail.component.html',
  styleUrls: ['./qr-batch-detail.component.scss'],
})
export class QrBatchDetailComponent implements OnInit, OnDestroy {
  links: PageLink[] = [
    {
      title: 'Product Catalog',
      path: 'products/list-published',
      isActive: false,
    },
    {
      title: '',
      path: '',
      isActive: false,
      isSeparator: true,
    },
    {
      title: 'QR Produk',
      path: 'products/list-qr-produk',
      isActive: false,
    },
    {
      title: '',
      path: '',
      isActive: false,
      isSeparator: true,
    },
  ];

  detailBatch$: Observable<BaseResponse<IQRBatchDetailItem>>;
  detailBatchInfo: IGenericLabelValue[] = [];
  loadingGetDetail = new BehaviorSubject(true);

  baseDatasource: BaseDatasource<IQRBatchDetailItem>;
  displayedColumns: string[];
  tableColumns: TableColumn[] = [
    {
      key: 'brand_id',
      title: 'Nama Produk',
      isSortable: false,
    },
    {
      key: 'generate_item_qty',
      title: 'QTY Generate',
      isSortable: false,
    },
  ];

  snapshotParamID: string;

  selectedQRBatchToDownload = new BehaviorSubject<IQrBatchItem | null>(null);
  @ViewChild('modalConfirmDownload') modalConfirmDownload: ModalConfirmDownloadComponent;

  private unsubscribe: Subscription[] = [];

  get PrivilegeDownloadBatch() {
    return this.qrProductService.hasDetailPrivilegeCTA('CTA_DOWNLOAD');
  }

  get PrivilegeViewListBrand() {
    return this.qrProductService.hasDetailPrivilegeCTA('VIEW_LIST_BRAND');
  }

  constructor(
    private pageInfoService: PageInfoService,
    private activatedRoute: ActivatedRoute,
    public utilitiesService: UtilitiesService,
    private qrProductService: QrProductsService,
    private baseTableService: BaseTableService<IQRBatchDetailItem>,
    private router: Router,
    private urlService: UrlUtilsService
  ) {
    this.snapshotParamID = this.activatedRoute.snapshot.params.id;
    this.detailBatch$ = this.qrProductService.getQRBatchDetail(this.snapshotParamID);
  }

  ngOnInit() {
    this.initPageInfo();
    this.setTableData();
  }

  initPageInfo() {
    this.pageInfoService.updateTitle('Detail Batch QR');

    const _detailSubs = this.detailBatch$
      .pipe(
        map((resp) => {
          if (resp && !resp.success) {
            this.loadingGetDetail.next(false);
          }

          return resp && resp.data;
        })
      )
      .subscribe((res) => {
        if (!res) {
          return;
        }

        this.mapDetailBatchInfo(res);
        this.selectedQRBatchToDownload.next(res);

        const { batch_name } = res;
        this.links.push({
          title: batch_name,
          path: '',
          isActive: true,
        });

        this.pageInfoService.updateBreadcrumbs(this.links);
      });

    this.unsubscribe.push(_detailSubs);
  }

  setTableData() {
    this.displayedColumns = this.tableColumns.map((item) => item.key);
    this.loadDataTable();
    const _responseSubs = this.baseTableService.responseDatabase$.subscribe((resp) => (this.baseDatasource = resp));
    this.unsubscribe.push(_responseSubs);
  }

  loadDataTable() {
    const param = this.urlService.sliceQueryParams();
    this.baseTableService.loadDataTable(API.LIST_BRAND_QR_BATCH_DETAIL + this.snapshotParamID, param ?? '');
  }

  mapDetailBatchInfo(data: IQRBatchDetailItem) {
    (Object.keys(EnumDetailBatchInfo) as (keyof typeof EnumDetailBatchInfo)[]).map((item) => {
      let _tempItemValue = data[item];

      if (item === 'create_date' || item === 'update_date') {
        _tempItemValue = this.utilitiesService.timeStampToDate(data[item], 'dd-MM-yyyy HH:mm:ss');
      }

      this.detailBatchInfo.push({ label: EnumDetailBatchInfo[item], value: _tempItemValue ?? '-' });
      this.loadingGetDetail.next(false);
    });
  }

  changePageEvent(e: BaseDatasource<IQrBatchItem>) {
    const { pageSize, current_page } = e;
    const dataParams: IQueryParams = {
      page: current_page,
      size: pageSize,
    };

    this.router
      .navigate([], {
        queryParams: {
          ...dataParams,
        },
        queryParamsHandling: 'merge',
      })
      .then(() => this.loadDataTable());
  }

  handleActionDownload() {
    return this.modalConfirmDownload.openConfirmDialog();
  }

  noteViewEstimationDate(availableDate: string = '2023-08-30T12:08:26.000+07:00') {
    const _date = this.utilitiesService.timeStampToDate(availableDate, 'dd-MM-yyyy HH:mm:ss');
    return `Batch QR sedang dalam proses pembuatan oleh sistem, dan estimasi selesai pada ${_date}.`;
  }

  isAbleToDownloadBatch = (status: string) => this.qrProductService.isAbleToDownloadBatch(status);

  isBatchOnProgress = (status: string) => this.qrProductService.isBatchOnProgress(status);

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  protected readonly STRING_CONSTANTS = STRING_CONSTANTS;
}
